import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { getEntityIcon, getCountry, resolveEntityTables } from "../lib/templates";
import DataTable from "../components/DataTable";
import EntityFormModal from "../components/EntityFormModal";
import SpacesView from "../components/SpacesView";
import PortfolioView from "../components/PortfolioView";
import OkrView from "../components/OkrView";
import DealsPipeline from "../components/DealsPipeline";
import DriveBrowser from "../components/drive/DriveBrowser";
import { HealthBadge, HealthSignals } from "../components/EntityHealth";
import { Modal, Button, Input, Spinner } from "../components/ui";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { ArrowLeft, Pencil, Trash2, Plus, AlertTriangle } from "lucide-react";

export default function EntityPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, lang, entities, entityHealth, loadEntities, wsSpaceScoped, wsRole } = useApp();
  const entity = entities.find((e) => e.key === key);
  const tables = resolveEntityTables(entity);
  // Space-scoped members (member/viewer) only see Spaces — no OKR / operational tables.
  const canManageEntity = !wsSpaceScoped && (wsRole === "owner" || wsRole === "super_admin" || wsRole === "admin");
  const allTabs = wsSpaceScoped ? ["spaces"] : ["spaces", "okr", "deals", ...tables];
  // active tab is driven by the URL (?tab=) so browser back/forward + refresh keep it
  const tabParam = searchParams.get("tab");
  const active = tabParam && allTabs.includes(tabParam) ? tabParam : "spaces";
  const setActive = (tb) => {
    const next = new URLSearchParams();
    if (tb !== "spaces") next.set("tab", tb);
    setSearchParams(next);  // switching tabs drops any ?account=
  };
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState("");

  const Icon = getEntityIcon(entity?.type);

  if (!entity) {
    return <div className="p-8 text-text-secondary">…</div>;
  }

  const countryObj = getCountry(entity.country);
  const health = entityHealth[entity.id];
  const badges = [
    countryObj ? (lang === "fr" ? countryObj.label.fr : countryObj.label.en) : null,
    entity.legal_form || null,
    entity.tax_regime ? entity.tax_regime.split(" ")[0] : null,
  ].filter(Boolean);

  const openDelete = () => {
    setConfirmName("");
    setDelError("");
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (confirmName.trim() !== entity.name) return;
    setDeleting(true);
    setDelError("");
    try {
      await api.delete(`/entities/${entity.id}`);
      await loadEntities();
      navigate("/");
    } catch {
      setDelError(lang === "fr" ? "Échec de la suppression. Réessayez." : "Delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const nameMatches = confirmName.trim() === entity.name;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4" /> {t("group_hq")}
      </button>

      <div className="flex items-start justify-between gap-4 mb-7">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${entity.color}22` }}>
            <Icon className="w-6 h-6" style={{ color: entity.color }} />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-medium tracking-tight flex items-center gap-3">
              {entity.name}
              {health ? <HealthBadge status={health.status} /> : <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entity.color }} />}
            </h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {entity.description && <span className="text-text-secondary text-sm">{entity.description}</span>}
              {badges.map((b) => (
                <span key={b} className="text-[11px] px-2 py-0.5 rounded-full border text-text-secondary">{b}</span>
              ))}
            </div>
            {health?.signals?.length > 0 && (
              <div className="mt-3">
                <HealthSignals signals={health.signals} entityKey={entity.key} />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canManageEntity && (
            <>
              <button onClick={() => setShowEdit(true)} data-testid="edit-entity-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors">
                <Pencil className="w-3.5 h-3.5" /> {t("edit")}
              </button>
              <button onClick={openDelete} data-testid="delete-entity-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-red-400 hover:bg-red-500/15 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> {t("delete")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b overflow-x-auto overflow-y-hidden no-scrollbar" data-testid="entity-tabs">
        {allTabs.map((tb) => (
          <button
            key={tb}
            onClick={() => setActive(tb)}
            data-testid={`tab-${tb}`}
            className={cn(
              "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
              active === tb
                ? "border-current text-text-primary"
                : "border-transparent text-text-secondary hover:text-text-primary"
            )}
            style={active === tb ? { borderColor: entity.color } : {}}
          >
            {tb === "spaces" ? t("tab_spaces") : tb === "okr" ? t("nav_okr") : tb === "deals" ? t("nav_deals") : t(`res_${tb}`)}
          </button>
        ))}
        {canManageEntity && (
          <button
            onClick={() => setShowEdit(true)}
            data-testid="add-section-button"
            title={t("add_section")}
            className="ml-1 mb-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors whitespace-nowrap"
          >
            <Plus className="w-3.5 h-3.5" /> {t("add_section")}
          </button>
        )}
      </div>

      {active === "spaces"
        ? <SpacesView entity={entity} />
        : active === "okr"
        ? <OkrView entityId={entity.id} key={`${entity.id}-okr`} />
        : active === "deals"
        ? <DealsPipeline entity={entity} key={`${entity.id}-deals`} />
        : active === "holdings"
        ? <PortfolioView entity={entity} key={`${entity.id}-portfolio`} />
        : active === "documents"
        ? <DriveBrowser rootId={`e:${entity.id}`} embedded key={`${entity.id}-docs`} />
        : <DataTable resource={active} entityId={entity.id} key={`${entity.id}-${active}`} />}

      <EntityFormModal
        open={showEdit}
        onClose={() => setShowEdit(false)}
        onSaved={() => loadEntities()}
        editing={entity}
      />

      <Modal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title={t("delete_entity")}
        footer={
          <>
            <Button variant="outline" onClick={() => setShowDelete(false)} data-testid="delete-cancel">
              {t("cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={!nameMatches || deleting}
              data-testid="confirm-delete-entity"
            >
              {deleting && <Spinner className="w-3.5 h-3.5" />}
              <Trash2 className="w-3.5 h-3.5" /> {t("delete_permanently")}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3 mb-4">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(239,68,68,0.15)" }}>
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </span>
          <p className="text-sm text-text-secondary">{t("delete_entity_warning")}</p>
        </div>

        <div className="rounded-lg border px-3 py-2.5 mb-4 flex items-center gap-2 text-sm" style={{ background: "var(--muted)" }}>
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entity.color }} />
          <span className="font-semibold">{entity.name}</span>
        </div>

        <label className="block text-xs font-medium text-text-secondary mb-1.5">
          {t("delete_entity_confirm_label")}
        </label>
        <Input
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && nameMatches) confirmDelete(); }}
          data-testid="delete-confirm-input"
          autoFocus
        />
        {delError && <p className="mt-2 text-xs text-red-400" data-testid="delete-error">{delError}</p>}
      </Modal>
    </div>
  );
}
